import { createElement, isValidElement, memo, type ReactElement, type ReactNode, useCallback, useId } from 'react';

import { CheckIcon, type IconComponent, MinusIcon } from '@air/design-icons';
import { Checkbox } from '@air/primitive-checkbox';

import { FILTER_ROW_CHECKBOX } from '~/constants/testIDs';

// biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
const filterRowIconClassName = 'shrink-0 ads:text-icon-default text-gray-10';

// biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
function isFilterRowIconComponent(icon: IconComponent | ReactElement): icon is IconComponent {
  return !isValidElement(icon);
}

// biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
function renderFilterRowIcon(icon: IconComponent | ReactElement): ReactNode {
  if (isValidElement(icon)) {
    return icon;
  }

  if (isFilterRowIconComponent(icon)) {
    return createElement(icon, { className: filterRowIconClassName, size: 'xs' });
  }

  return null;
}

export type FilterRowWithCheckboxProps = {
  icon?: IconComponent | ReactElement;
  label: string;
  isPartialSelected?: boolean;
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
};

export const FilterRowWithCheckbox = memo(
  ({ icon, onChange, label, isPartialSelected, isSelected }: FilterRowWithCheckboxProps) => {
    const id = useId();

    const renderIndicator = useCallback(() => {
      if (isPartialSelected) {
        return <MinusIcon size="xs" />;
      }

      return <CheckIcon size="xs" />;
    }, [isPartialSelected]);

    return (
      <label className="mb-0.5 flex cursor-pointer justify-between py-1.5 pr-1 pl-1.5" htmlFor={id}>
        <div className="flex items-center gap-2">
          {icon ? renderFilterRowIcon(icon) : null}
          <span className="ads:text-primary text-14 text-gray-12">{label}</span>
        </div>

        <Checkbox
          id={id}
          renderIndicator={renderIndicator}
          aria-label={label}
          checked={isSelected}
          // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
          data-testid={`${FILTER_ROW_CHECKBOX}-${label}`}
          onCheckedChange={onChange}
        />
      </label>
    );
  },
);

FilterRowWithCheckbox.displayName = 'FilterRowWithCheckbox';
